import {DEFAULT_COL_WIDTH} from '../constants/defaultColWidth';
import type {EagColumn} from '../types/EagColumn';
import type {EagColumnState} from '../types/EagColumnState';
import {isGroupRowCellId, type GroupRowModel} from '../utils/groupRowModel';

export type DragOverlayModel = {
  headerName: string;
  width: number;
};

/**
 * A dragged group's chip is capped so wide groups (sum of child widths) don't
 * produce an unwieldy overlay following the pointer.
 */
const GROUP_OVERLAY_MAX_WIDTH = 320;

/**
 * Resolves the header chip shown in the dnd-kit DragOverlay: the dragged
 * column's header (column drag) or the group name over the block's summed
 * width (group-header drag). Null when nothing is dragged or the id doesn't
 * resolve.
 */
export const useDragOverlay = (
  activeDragId: string | null | undefined,
  visibleColumnMap: Map<string, EagColumn>,
  visibleColumnStateMap: Map<string, EagColumnState>,
  groupRowModel: GroupRowModel | null,
  columnWidthMap: Map<string, number>
): DragOverlayModel | null => {
  if (!activeDragId) {
    return null;
  }

  if (isGroupRowCellId(activeDragId)) {
    const cell = groupRowModel?.cellById.get(activeDragId);
    if (!cell || cell.type !== 'group') {
      return null;
    }
    const width = cell.columnKeys.reduce(
      (sum, key) => sum + (columnWidthMap.get(key) ?? DEFAULT_COL_WIDTH),
      0
    );
    return {headerName: cell.headerName, width: Math.min(width, GROUP_OVERLAY_MAX_WIDTH)};
  }

  const column = visibleColumnMap.get(activeDragId);
  const columnState = visibleColumnStateMap.get(activeDragId);
  if (!column || !columnState) {
    return null;
  }
  return {headerName: column.headerName, width: columnState.width ?? DEFAULT_COL_WIDTH};
};
